export type TrafficStatus = "ok" | "warning" | "critical" | "unavailable";

/** Validated daily traffic used by the flag card. */
export interface TrafficChange {
	id: string;
	clientId?: number;
	bucket?: string;
	previousBucket?: string;
	label: string;
	/** Signed relative change in percent: -50 means traffic decreased by 50%. */
	changePercent: number | null;
	currentCount?: number;
	previousCount?: number;
	fromZero?: boolean;
	lowVolume?: boolean;
}

export interface OverviewAnomalies {
	changes: TrafficChange[];
	bucket: string;
	previousBucket: string;
	asOf?: string;
	minCount: number;
}

export interface AnomaliesScope {
	targetId: number;
	targetType: "client" | "merchant" | "project";
	projectId: number;
	userId: number;
}

export const TRAFFIC_WARNING_PERCENT = 30;
export const TRAFFIC_CRITICAL_PERCENT = 50;

export function getTrafficStatus(changePercent: number | null): TrafficStatus {
	if (changePercent === null || !Number.isFinite(changePercent)) return "unavailable";
	const magnitude = Math.abs(changePercent);
	if (magnitude >= TRAFFIC_CRITICAL_PERCENT) return "critical";
	if (magnitude >= TRAFFIC_WARNING_PERCENT) return "warning";
	return "ok";
}

export function getOverallTrafficStatus(changes: TrafficChange[] | null): TrafficStatus {
	if (!changes?.length) return "unavailable";
	// Explicitly excluded low-volume clients do not invalidate assessed traffic.
	const statuses = changes.filter(change => !change.lowVolume).map(getTrafficChangeStatus);
	if (!statuses.length) return "unavailable";
	if (statuses.includes("critical")) return "critical";
	if (statuses.includes("warning")) return "warning";
	if (statuses.includes("unavailable")) return "unavailable";
	return "ok";
}

export function getTrafficChangeStatus(change: TrafficChange): TrafficStatus {
	if (change.lowVolume) return "unavailable";
	if (change.fromZero) return "critical";
	return getTrafficStatus(change.changePercent);
}

export function getTrafficProblems(changes: TrafficChange[] | null): TrafficChange[] {
	return (changes ?? []).filter(change => {
		const status = getTrafficChangeStatus(change);
		return status === "critical" || status === "warning";
	});
}

export function describeTrafficChange(change: TrafficChange): string {
	if (change.fromZero) return "Рост с нуля";
	const percent = change.changePercent;
	if (percent === null || !Number.isFinite(percent)) return "Нет данных для сравнения";
	if (percent === 0) return "Без изменений";
	return `${percent > 0 ? "Рост" : "Снижение"} на ${Math.abs(percent).toLocaleString("ru-RU", { maximumFractionDigits: 2 })}%`;
}

export function getTrafficSummary(changes: TrafficChange[] | null): string {
	const problems = getTrafficProblems(changes);
	const total = problems.find(change => change.id === "total");
	const clients = problems.filter(change => change.id !== "total");
	if (problems.length === 1) return `${problems[0].label}: ${describeTrafficChange(problems[0])}`;
	if (problems.length) return [total ? `${total.label}: ${describeTrafficChange(total)}` : undefined,
		clients.length ? `Клиентов с отклонениями: ${clients.length}` : undefined].filter(Boolean).join(" · ");
	return getOverallTrafficStatus(changes) === "ok" ? "Значимых отклонений за день не найдено" : "Недостаточно данных для оценки трафика";
}

export function getTrafficComparisonPeriod(result: OverviewAnomalies): { bucket: string; previousBucket: string } | null {
	const periods = result.changes.map(change => ({ bucket: change.bucket ?? result.bucket, previousBucket: change.previousBucket ?? result.previousBucket }));
	const first = periods[0];
	return first && periods.every(period => period.bucket === first.bucket && period.previousBucket === first.previousBucket) ? first : null;
}

export function getAnomaliesRequest(scope: AnomaliesScope) {
	return {
		method: "overview/anomalies",
		httpMethod: "POST" as const,
		apiVersion: 2 as const,
		expectedProjectId: scope.projectId,
		headers: { ignoreBusy: "true" },
		data: {
			scope: "day" as const,
			...(scope.targetType === "client" ? { client_id: scope.targetId } : {}),
			...(scope.targetType === "merchant" ? { mid: scope.targetId } : {}),
		},
	};
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const isCount = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const DAY_MS = 86_400_000;
const PERCENT_SCALE = 100;
const API_PERCENT_ROUNDING = 0.11;

/** API timestamps have no timezone; preserve the server's calendar dates. */
export function formatAnomaliesDate(value: string): string {
	const [year, month, day] = value.slice(0, 10).split("-");
	return `${day}.${month}.${year}${value.length > 10 ? value.slice(10) : ""}`;
}

function parseBucket(value: unknown): string | null {
	if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2} 00:00:00$/.test(value)) return null;
	const day = value.slice(0, 10);
	const time = Date.parse(`${day}T00:00:00Z`);
	return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === day ? day : null;
}

function parseChange(value: unknown, period: { bucket: string; previousBucket: string }, totalLabel?: string): TrafficChange | null {
	if (!isRecord(value) || !isCount(value.curr) || !isCount(value.prev)
		|| typeof value.from_zero !== "boolean" || typeof value.low_volume !== "boolean") return null;
	if (value.from_zero !== (value.prev === 0 && value.curr > 0)) return null;
	const delta = value.delta_pct;
	if (delta !== null && (typeof delta !== "number" || !Number.isFinite(delta))) return null;
	if (value.prev === 0 && delta !== null) return null;
	if (value.prev > 0 && typeof delta === "number"
		&& Math.abs(delta - (value.curr - value.prev) / value.prev * PERCENT_SCALE) > API_PERCENT_ROUNDING) return null;
	if (!totalLabel && (!isCount(value.client_id) || value.client_id === 0)) return null;
	// A row may carry its own explicit comparison dates; otherwise use day.*.
	// Historical series do not establish which buckets curr/prev describe.
	const ownPeriod = "bucket" in value || "prev_bucket" in value;
	const bucket = ownPeriod ? parseBucket(value.bucket) : period.bucket;
	const previousBucket = ownPeriod ? parseBucket(value.prev_bucket) : period.previousBucket;
	if (!bucket || !previousBucket || Date.parse(bucket) - Date.parse(previousBucket) !== DAY_MS) return null;
	return {
		id: totalLabel ? "total" : `client-${value.client_id}`,
		...(!totalLabel && isCount(value.client_id) ? { clientId: value.client_id } : {}),
		bucket,
		previousBucket,
		label: totalLabel ?? (typeof value.client_name === "string" && value.client_name.trim() ? value.client_name.trim() : `Клиент ${value.client_id}`),
		changePercent: delta,
		currentCount: value.curr,
		previousCount: value.prev,
		fromZero: value.from_zero,
		lowVolume: value.low_volume,
	};
}

export function parseOverviewAnomalies(data: unknown, scope?: AnomaliesScope): OverviewAnomalies | null {
	if (!isRecord(data) || data.error) return null;
	// HTTP may unwrap the standard envelope; also accept the supplied { data: ... }.
	const payload = "day" in data ? data : data.data;
	if (!isRecord(payload) || payload.error || !isRecord(payload.day) || !isCount(payload.min_count)) return null;
	const day = payload.day;
	const bucket = parseBucket(day.bucket);
	const previousBucket = parseBucket(day.prev_bucket);
	if (!bucket || !previousBucket || Date.parse(bucket) - Date.parse(previousBucket) !== DAY_MS || !Array.isArray(day.clients)) return null;
	const period = { bucket, previousBucket };
	let changes: TrafficChange[];
	if (scope?.targetType === "client") {
		// Never show another client's row or an unscoped total on a client page.
		const matches = day.clients.filter(value => isRecord(value) && value.client_id === scope.targetId);
		if (matches.length > 1) return null;
		const source = matches[0] ?? (isRecord(day.total) && day.total.client_id === scope.targetId ? day.total : null);
		const change = parseChange(source, period);
		if (!change) return null;
		changes = [change];
	} else {
		const total = parseChange(day.total, period, scope?.targetType === "merchant" ? "По мерчанту" : "Всего по проекту");
		if (!total) return null;
		const clients = scope?.targetType === "merchant" ? [] : day.clients.map(value => parseChange(value, period));
		if (clients.some(value => value === null)) return null;
		const rows = clients as TrafficChange[];
		if (new Set(rows.map(row => row.id)).size !== rows.length) return null;
		const priority: Record<TrafficStatus, number> = { critical: 0, warning: 1, ok: 2, unavailable: 3 };
		rows.sort((a, b) => priority[getTrafficChangeStatus(a)] - priority[getTrafficChangeStatus(b)]);
		changes = [total, ...rows];
	}
	const asOf = typeof payload.as_of === "string" && /^\d{4}-\d{2}-\d{2} [0-2]\d:[0-5]\d:[0-5]\d$/.test(payload.as_of) ? payload.as_of : undefined;
	return { changes, bucket, previousBucket, asOf, minCount: payload.min_count };
}
